/**
 * Microsoft Graph API utility functions
 * Handles API calls to get user profile information and photos
 */

import { graphConfig } from '../auth/authConfig';

/**
 * Interface for user profile data from Graph API
 */
export interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  photo?: string | null; // Base64 encoded photo data or null if no photo
}

/**
 * Calls the Microsoft Graph API to get user profile information
 * @param accessToken - Valid access token with User.Read scope
 * @returns Promise<GraphUser> - User profile data
 */
export async function getUserProfile(accessToken: string): Promise<GraphUser> {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;
  headers.append('Authorization', bearer);

  const options = {
    method: 'GET',
    headers: headers,
  };

  try {
    const response = await fetch(graphConfig.graphMeEndpoint, options);
    
    if (!response.ok) {
      throw new Error(`Graph API request failed: ${response.status} ${response.statusText}`);
    }

    const userData = await response.json();
    return userData as GraphUser;
  } catch (error) {
    console.error('Error fetching user profile from Graph API:', error);
    throw error;
  }
}

/**
 * Calls the Microsoft Graph API to get user profile photo
 * @param accessToken - Valid access token with User.Read scope
 * @returns Promise<string | null> - Base64 encoded photo data or null if no photo
 */
export async function getUserPhoto(accessToken: string): Promise<string | null> {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;
  headers.append('Authorization', bearer);

  const options = {
    method: 'GET',
    headers: headers,
  };

  try {
    const response = await fetch(graphConfig.graphPhotoEndpoint, options);
    
    if (response.status === 404) {
      // No photo available for this user
      console.log('No profile photo available for user');
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`Graph API photo request failed: ${response.status} ${response.statusText}`);
    }

    const photoBlob = await response.blob();
    
    // Convert blob to base64 data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(photoBlob);
    });
  } catch (error) {
    console.error('Error fetching user photo from Graph API:', error);
    return null; // Return null instead of throwing to gracefully handle missing photos
  }
}

/**
 * Gets complete user profile including photo
 * @param accessToken - Valid access token with User.Read scope
 * @returns Promise<GraphUser> - Complete user profile with photo
 */
export async function getCompleteUserProfile(accessToken: string): Promise<GraphUser> {
  try {
    // Get basic profile data
    const userProfile = await getUserProfile(accessToken);
    
    // Try to get photo
    const photoData = await getUserPhoto(accessToken);
    
    return {
      ...userProfile,
      photo: photoData
    };
  } catch (error) {
    console.error('Error fetching complete user profile:', error);
    throw error;
  }
}
